import type { Metadata } from 'next'

/** Canonical site origin — metadataBase / JSON-LD / OG */

const SITE_TITLE_SUFFIX = ' | Haber.com'

/** Root layout `title.template` zaten ekler — sayfa title'ından tekrarları temizle. */
export function stripSiteTitleSuffix(title: string): string {
  return title.replace(/(?:\s*(?:\||-)\s*Haber\.com)+\s*$/i, '').trim()
}

/** Tek `| Haber.com` ekli tam başlık (absolute / document.title / OG). */
export function absoluteSiteTitle(title: string): string {
  const base = stripSiteTitleSuffix(title)
  return base ? `${base}${SITE_TITLE_SUFFIX}` : 'Haber.com'
}

export function getSiteOrigin(): string {
  return (process.env.NEXT_PUBLIC_SITE_URL || process.env.NEXT_PUBLIC_SITE_ORIGIN || 'https://www.haber.com').replace(
    /\/$/,
    '',
  )
}

export function absoluteUrl(path = '/'): string {
  const origin = getSiteOrigin()
  if (!path || path === '/') return origin
  return path.startsWith('http') ? path : `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function getDefaultSocialImage() {
  return {
    url: absoluteUrl('/opengraph-image'),
    width: 1200,
    height: 630,
    alt: 'Haber.com — Son dakika ve güncel haberler',
  }
}

/** Google News / Organization logo — raster (PNG), geniş wordmark tercih */
export function getPublisherLogo() {
  const origin = getSiteOrigin()
  return {
    '@type': 'ImageObject' as const,
    url: `${origin}/publisher-logo-wide.png`,
    width: 600,
    height: 160,
  }
}

/** Resmi sosyal / yayın profilleri — Knowledge Graph sameAs (yalnızca doğrulanmış hesaplar) */
const DEFAULT_SAME_AS = [
  'https://www.facebook.com/comhaber',
  'https://x.com/habercomsosyal',
  'https://www.instagram.com/habercomsosyal',
  'https://www.youtube.com/@haber_com',
  'https://www.linkedin.com/company/habercom',
  'https://news.google.com/publications/CAAqIggKIhxDQklTRHdnTWFnc0tDV2hoWW1WeUxtTnZiU2dBUAE?hl=tr&gl=TR&ceid=TR:tr',
]

const ORG_DESCRIPTION =
  'Haber.com (www.haber.com), Türkiye merkezli bağımsız bir dijital haber kuruluşudur. Son dakika, gündem, ekonomi, spor ve dünya haberlerini yayınlar. Resmi alan adı haber.com’dur; benzer isimli diğer sitelerle bağlantılı değildir.'

function getSameAsLinks(): string[] {
  const raw = process.env.NEXT_PUBLIC_ORG_SAME_AS?.trim()
  if (!raw) return DEFAULT_SAME_AS
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^https?:\/\//i.test(s))
}

/** Sayfa Open Graph tanımları layout alanlarını ezdiği için tüm alanları birlikte üret. */
export function buildPageSocialMetadata(opts: {
  title: string
  description: string
  url: string
}): Pick<Metadata, 'openGraph' | 'twitter'> {
  const title = absoluteSiteTitle(opts.title)
  const url = absoluteUrl(opts.url)
  const image = getDefaultSocialImage()
  const twitterSite = process.env.NEXT_PUBLIC_TWITTER_SITE?.trim() || '@habercomsosyal'
  return {
    openGraph: {
      title,
      description: opts.description,
      url,
      type: 'website',
      locale: 'tr_TR',
      siteName: 'Haber.com',
      images: [image],
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description: opts.description,
      images: [image.url],
      site: twitterSite,
    },
  }
}

/** Admin SEO başlığı; boşsa görünen başlığa düş. */
export function pickSeoTitle(seoTitle: string | null | undefined, fallback: string): string {
  const seo = (seoTitle || '').trim()
  return stripSiteTitleSuffix(seo || fallback.trim())
}

export function buildOrganizationJsonLd() {
  const origin = getSiteOrigin()
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    '@id': `${origin}/#organization`,
    name: 'Haber.com',
    alternateName: ['Habercom', 'haber.com', 'haber com', 'www.haber.com'],
    legalName: 'HABER.COM MEDIA LTD',
    url: origin,
    logo: getPublisherLogo(),
    image: getPublisherLogo(),
    description: ORG_DESCRIPTION,
    disambiguatingDescription:
      'Türkiye’de yalnızca haber.com alan adında yayın yapan dijital haber sitesi. Benzer isimli başka sitelerle aynı kuruluş değildir.',
    foundingLocation: {
      '@type': 'Place',
      name: 'İstanbul, Türkiye',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'İstanbul',
      addressCountry: 'TR',
    },
    email: 'info@haber.com',
    areaServed: {
      '@type': 'Country',
      name: 'Türkiye',
    },
    inLanguage: 'tr-TR',
    knowsAbout: [
      'Son dakika haberleri',
      'Gündem',
      'Ekonomi',
      'Spor',
      'Dünya haberleri',
      'Teknoloji',
    ],
    sameAs: getSameAsLinks(),
    about: absoluteUrl('/hakkimizda'),
    publishingPrinciples: absoluteUrl('/kunye'),
    ethicsPolicy: absoluteUrl('/kunye'),
    ownershipFundingInfo: absoluteUrl('/kunye'),
    masthead: absoluteUrl('/kunye'),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'info@haber.com',
        url: absoluteUrl('/iletisim'),
        availableLanguage: ['Turkish', 'tr'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'editorial',
        url: absoluteUrl('/kunye'),
        availableLanguage: ['Turkish', 'tr'],
      },
    ],
  }
}

export function buildWebSiteJsonLd() {
  const origin = getSiteOrigin()
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${origin}/#website`,
    name: 'Haber.com',
    alternateName: ['haber.com', 'haber com', 'www.haber.com'],
    url: origin,
    description: ORG_DESCRIPTION,
    inLanguage: 'tr-TR',
    publisher: { '@id': `${origin}/#organization` },
    about: { '@id': `${origin}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        // /arama?q= → buildSearchPageUrl redirect (app/arama/page.tsx)
        urlTemplate: `${origin}/arama?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/** Anasayfa / kurumsal: Organization + WebSite tek @graph (entity sinyali) */
export function buildEntityGraphJsonLd() {
  const org = buildOrganizationJsonLd()
  const site = buildWebSiteJsonLd()
  const { '@context': _c1, ...orgNode } = org
  const { '@context': _c2, ...siteNode } = site
  return {
    '@context': 'https://schema.org',
    '@graph': [orgNode, siteNode],
  }
}

/** /hakkimizda — AboutPage + Organization bağlantısı */
export function buildAboutPageJsonLd() {
  const origin = getSiteOrigin()
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${origin}/hakkimizda#webpage`,
    url: absoluteUrl('/hakkimizda'),
    name: 'Hakkımızda — Haber.com',
    description: ORG_DESCRIPTION,
    inLanguage: 'tr-TR',
    isPartOf: { '@id': `${origin}/#website` },
    about: { '@id': `${origin}/#organization` },
    mainEntity: { '@id': `${origin}/#organization` },
    publisher: { '@id': `${origin}/#organization` },
  }
}

export function buildArticleBreadcrumbJsonLd(opts: {
  categoryTitle: string
  categorySlug: string
  articleTitle: string
  canonicalUrl: string
}) {
  const origin = getSiteOrigin()
  const catSlug = (opts.categorySlug || 'haber').trim() || 'haber'
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Ana Sayfa',
        item: origin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: opts.categoryTitle || 'Haber',
        item: absoluteUrl(`/${catSlug}`),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: opts.articleTitle,
        item: opts.canonicalUrl,
      },
    ],
  }
}

/** Search Console / Bing / Yandex — env ile */
export function getSiteVerificationMetadata(): Metadata['verification'] {
  const google = process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION?.trim()
  const yandex = process.env.NEXT_PUBLIC_YANDEX_VERIFICATION?.trim()
  const bing = process.env.NEXT_PUBLIC_BING_SITE_VERIFICATION?.trim()
  if (!google && !yandex && !bing) return undefined
  return {
    ...(google ? { google } : {}),
    ...(yandex ? { yandex } : {}),
    ...(bing ? { other: { 'msvalidate.01': bing } } : {}),
  }
}
